/**
 * NotificationCenter — In-app notification bell with dropdown
 * Shows CLV alerts, action plan updates, and system notifications.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Badge } from '@/components/ui/badge';
import { API_URL } from '@/config/apiConfig';
import {
  Bell, BellRing, AlertTriangle, CheckCircle2, Info, XCircle,
  X, ChevronRight, Clock
} from 'lucide-react';

export default function NotificationCenter() {
  const [unread, setUnread] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  // Poll unread count
  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch(`${API_URL}/api/notification-center/unread-count`, { credentials: 'include'});
        if (res.ok) {
          const data = await res.json();
          setUnread(data.unread || 0);
        }
      } catch (_) {}
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/notification-center/inbox?limit=20`, { credentials: 'include'});
      if (res.ok) setNotifications((await res.json()).notifications || []);
    } catch (_) {}
    setLoading(false);
  };

  const togglePanel = () => {
    if (!open) loadNotifications();
    setOpen(!open);
  };

  const markRead = async (id) => {
    try {
      await fetch(`${API_URL}/api/notification-center/mark-read/${id}`, { credentials: 'include', method: 'POST'});
      setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch (_) {}
  };

  const markAllRead = async () => {
    try {
      await fetch(`${API_URL}/api/notification-center/mark-all-read`, { credentials: 'include', method: 'POST'});
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch (_) {}
  };

  const typeIcons = {
    critical: XCircle,
    warning: AlertTriangle,
    success: CheckCircle2,
    info: Info
  };
  const typeColors = {
    critical: 'text-red-500 bg-red-500/10',
    warning: 'text-amber-500 bg-amber-500/10',
    success: 'text-emerald-500 bg-emerald-500/10',
    info: 'text-blue-500 bg-blue-500/10'
  };

  return (
    <div className="relative" ref={ref} data-testid="notification-center">
      {/* Bell Button */}
      <button onClick={togglePanel} className="relative p-2 rounded-lg hover:bg-muted/50 transition-colors" data-testid="notification-bell">
        {unread > 0 ? (
          <BellRing className="w-5 h-5 text-amber-500" />
        ) : (
          <Bell className="w-5 h-5 text-muted-foreground" />
        )}
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 rounded-full bg-red-500 text-[9px] text-white flex items-center justify-center font-bold min-w-[18px] px-1">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div className="absolute right-0 top-12 w-96 z-50 rounded-xl border border-border/40 bg-background shadow-2xl overflow-hidden" data-testid="notification-panel">
          {/* Header */}
          <div className="p-3 border-b border-border/20 flex items-center justify-between bg-muted/30">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              <span className="text-sm font-semibold">Notifications</span>
              {unread > 0 && <Badge variant="destructive" className="text-[10px] px-1.5">{unread}</Badge>}
            </div>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={markAllRead} className="text-[10px] text-primary hover:underline">Mark all read</button>
              )}
              <button onClick={() => setOpen(false)}><X className="w-4 h-4 text-muted-foreground" /></button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto divide-y divide-border/10">
            {notifications.length === 0 && !loading && (
              <div className="p-8 text-center">
                <Bell className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-20" />
                <p className="text-sm text-muted-foreground">No notifications yet</p>
              </div>
            )}
            {notifications.map(n => {
              const Icon = typeIcons[n.type] || Info;
              const color = typeColors[n.type] || typeColors.info;
              return (
                <div
                  key={n.notification_id}
                  className={`p-3 flex items-start gap-3 hover:bg-muted/20 transition-colors cursor-pointer ${!n.read ? 'bg-blue-500/5' : ''}`}
                  onClick={() => {
                    if (!n.read) markRead(n.notification_id);
                    if (n.action_url) window.location.href = n.action_url;
                  }}
                  data-testid={`notif-${n.notification_id}`}
                >
                  <div className={`p-1.5 rounded-lg flex-shrink-0 ${color}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-xs font-medium ${!n.read ? 'text-foreground' : 'text-muted-foreground'}`}>{n.title}</p>
                      {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" />
                      {new Date(n.created_at).toLocaleString()}
                    </p>
                  </div>
                  {n.action_url && <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
